import * as React from "react";
import { ChevronDown, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface OdsSelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
  containerClassName?: string;
  error?: React.ReactNode;
  info?: React.ReactNode;
  label?: React.ReactNode;
  message?: React.ReactNode;
  requiredIndicator?: boolean;
  selectClassName?: string;
  showIcon?: boolean;
}

export const OdsSelect = React.forwardRef<HTMLSelectElement, OdsSelectProps>(
  (
    {
      children,
      className,
      containerClassName,
      disabled,
      error,
      id,
      info,
      label,
      message,
      requiredIndicator = false,
      selectClassName,
      showIcon = true,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const selectId = id ?? generatedId;
    const helperText = error ?? message;
    const isDestructive = Boolean(error);

    return (
      <div className={cn("flex flex-col gap-2", containerClassName)}>
        {label ? (
          <label
            htmlFor={selectId}
            className="flex items-center gap-1 text-[14px] font-medium leading-[120%] text-[var(--fg-secondary)]"
          >
            <span>{label}</span>
            {requiredIndicator ? <span className="text-[var(--fg-primary)]">*</span> : null}
            {info ? (
              <span title={typeof info === "string" ? info : undefined}>
                <Info className="h-3.5 w-3.5 text-[var(--fg-secondary)]" />
              </span>
            ) : null}
          </label>
        ) : null}

        <div
          className={cn(
            "relative flex h-10 w-full items-center rounded-lg bg-[var(--bg-secondary)] transition-[border-color,opacity]",
            isDestructive ? "border border-[var(--border-negative)]" : "border border-transparent focus-within:border-[var(--border-primary)]",
            disabled && "opacity-50",
            className
          )}
        >
          <select
            id={selectId}
            ref={ref}
            disabled={disabled}
            aria-invalid={isDestructive || undefined}
            className={cn(
              "h-full w-full min-w-0 appearance-none bg-transparent px-3 text-[14px] font-medium leading-[120%] text-[var(--fg-base)] outline-none disabled:cursor-not-allowed",
              showIcon && "pr-10",
              selectClassName
            )}
            {...props}
          >
            {children}
          </select>
          {showIcon ? <ChevronDown className="pointer-events-none absolute right-3 h-4 w-4 text-[var(--fg-secondary)]" /> : null}
        </div>

        {helperText ? (
          <p className={cn("text-[12px] font-medium leading-[120%]", isDestructive ? "text-[var(--fg-negative)]" : "text-[var(--fg-secondary)]")}>
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

OdsSelect.displayName = "OdsSelect";
